import React, { useMemo } from 'react';
import { Button, makeStyles, TableCell, Typography } from '@material-ui/core';

import { colors } from 'src/constants';
import { copyToClipboard } from 'src/utils';

import { PoweredBuildsPriceListData, ProductPriceListData } from 'src/types';
import { TypographyPriceList } from '../TypographyPriceList';
import { getCategoryColor } from 'src/utils/categories/getCategoryColor';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { usePriceList } from 'src/hooks/priceList';
import { isArray } from 'lodash';

interface TableCellNameProps {
  branch_ids?: number[];
  id?: string;
  data: ProductPriceListData & PoweredBuildsPriceListData;
  index: number;
  isLoading?: boolean;
  isPoweredBuildsTable?: boolean;
  priceList?: ProductPriceListData[];
  canViewSalesPriceList?: boolean;
}

const useStyles = makeStyles(
  {
    tableCellBorder: {
      borderRight: '0.5px solid black',
      borderColor: colors.primary
    }
  },
  { index: 1 }
);

export const Component = ({
  index,
  data,
  branch_ids,
  isPoweredBuildsTable = false,
  canViewSalesPriceList = false
}: TableCellNameProps) => {
  const classes = useStyles();
  const { onHandlePoweredBuildsProductIds } = usePriceList();

  const bundleOrProductName = isPoweredBuildsTable
    ? data?.bundle_name
    : data?.product_name;

  const colorVariant = useMemo(() => (index % 2 ? '100' : '200'), [index]);

  const backgroundColor = useMemo(
    () => getCategoryColor(data.category_id || 0)[colorVariant],
    [colorVariant, data.category_id]
  );

  const onCopyText = (text: string) => {
    const newText = `${text}`;
    copyToClipboard(newText);
  };

  const onHandleShowPcBundleComponents = (
    prod_ids?: string[],
    bundle_id?: number
  ) => {
    onHandlePoweredBuildsProductIds({
      open: true,
      product_ids: prod_ids || [],
      bundle_id: bundle_id || undefined
    });
  };

  return (
    <TableCell
      style={{ backgroundColor }}
      className={classes.tableCellBorder}
      onClick={(e) => e.stopPropagation()}
    >
      {isPoweredBuildsTable ? (
        <div
          style={{
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'start',
            alignItems: 'center'
          }}
        >
          <Button
            style={{ padding: 0, margin: 0, width: 0 }}
            startIcon={<FileCopyIcon style={{ margin: 0, padding: 0 }} />}
            onClick={() => onCopyText(data?.bundle_name || '')}
          />
          <div
            onClick={() => {
              if (data?.product_ids && isArray(data?.product_ids)) {
                onHandleShowPcBundleComponents(
                  data.product_ids || [],
                  data?.id
                );
              }
            }}
          >
            <Typography variant="h5">{data?.bundle_name}</Typography>
          </div>
        </div>
      ) : (
        <TypographyPriceList
          data={data}
          link={
            canViewSalesPriceList
              ? isPoweredBuildsTable
                ? `/app/pc-bundles/components/${data?.id}`
                : `/app/products/${data?.id}`
              : undefined
          }
          text={bundleOrProductName || ''}
          subText={
            canViewSalesPriceList || isPoweredBuildsTable
              ? undefined
              : `SKU: ${data?.sku || ''}`
          }
          onCopy={() => {
            onCopyText(bundleOrProductName || '');
          }}
          branchIds={branch_ids}
        />
      )}
    </TableCell>
  );
};

export const Name = React.memo(Component);
